/**
 * @fileoverview Per-page privilege guard for any dashboard-area module's routes.
 * @description Redirects to /403 when the signed-in user's GET /identity/me privileges don't
 * include the one this route requires. Lives in app/ (not shared/lib/auth alongside
 * ProtectedRoute) because it needs useCurrentSession from features/auth, and shared/ may not
 * import a feature (ADR-0002). Safe to assume the query has already resolved by the time this
 * renders — every gated route sits under DashboardShell, which blocks rendering until
 * GET /identity/me resolves (see DashboardShell.tsx), so there's no pending/error state to handle
 * here. Shared by both the sales and banking modules (originally sales-only, generalized once a
 * second module needed the identical guard).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { Navigate, Outlet } from 'react-router'

import { useCurrentSession } from '@/features/auth'
import { ROUTES } from '@/shared/constants/routes.constants'

interface RequirePrivilegeProps {
  privilege: string
}

/**
 * @component
 * @param {RequirePrivilegeProps} props - The privilege key this route requires.
 * @param {string} props.privilege - A key from GET /identity/me's privileges list.
 */
function RequirePrivilege({ privilege }: RequirePrivilegeProps) {
  const currentSessionQuery = useCurrentSession()
  const privileges = currentSessionQuery.data?.privileges ?? []

  if (!privileges.includes(privilege)) {
    return <Navigate to={ROUTES.FORBIDDEN} replace />
  }

  return <Outlet />
}

export default RequirePrivilege
